import React, { useState, useEffect, useCallback } from 'react';
import {
  listRatingsWithDetails,
  updateComplaintStatus,
  deleteRating,
  computeRatingStats,
} from '../services/adminDeliveryRatingsService';
import './DeliveryRatingsPage.css';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const Stars = ({ rating, size = 16 }) => {
  const n = Math.round(rating || 0);
  return (
    <span className="drp-stars" style={{ fontSize: size }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} className={i <= n ? 'drp-star-filled' : 'drp-star-empty'}>★</span>
      ))}
    </span>
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <div className="drp-stat-card">
    <span className="drp-stat-label">{label}</span>
    <span className="drp-stat-value" style={color ? { color } : {}}>{value}</span>
    {sub && <span className="drp-stat-sub">{sub}</span>}
  </div>
);

const COMPLAINT_STATUSES = ['pending', 'investigating', 'resolved', 'dismissed'];

const DeliveryRatingsPage = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('all'); // all | complaints | ratings
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRating, setSelectedRating] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listRatingsWithDetails();
      setRatings(data);
    } catch (err) {
      setError(err.message || 'Failed to load ratings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (ratingId, newStatus) => {
    setUpdatingId(ratingId);
    try {
      await updateComplaintStatus(ratingId, newStatus);
      setRatings(prev => prev.map(r => r.$id === ratingId ? { ...r, complaintStatus: newStatus } : r));
      if (selectedRating?.$id === ratingId) {
        setSelectedRating(prev => ({ ...prev, complaintStatus: newStatus }));
      }
    } catch (err) {
      alert(err.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (ratingId) => {
    if (!window.confirm('Delete this rating/feedback permanently?')) return;
    try {
      await deleteRating(ratingId);
      setRatings(prev => prev.filter(r => r.$id !== ratingId));
      if (selectedRating?.$id === ratingId) setSelectedRating(null);
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const stats = computeRatingStats(ratings.filter(r => !r.isComplaint));
  const complaintStats = computeRatingStats(ratings.filter(r => r.isComplaint));

  let filtered = ratings;
  if (tab === 'complaints') filtered = filtered.filter(r => r.isComplaint);
  if (tab === 'ratings') filtered = filtered.filter(r => !r.isComplaint);
  if (statusFilter) filtered = filtered.filter(r => r.complaintStatus === statusFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(r =>
      (r.driverId || '').toLowerCase().includes(q) ||
      (r.comment || '').toLowerCase().includes(q) ||
      (r.customerId || '').toLowerCase().includes(q) ||
      (r.requestId || '').toLowerCase().includes(q)
    );
  }

  return (
    <div className="drp-page">
      <div className="drp-header">
        <div>
          <h1>Delivery Ratings & Feedback</h1>
          <p className="drp-sub">Customer ratings and complaints for delivery service</p>
        </div>
        <button className="drp-btn-refresh" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="drp-error">{error}</div>}

      {/* Stats */}
      <div className="drp-stats">
        <StatCard label="Total Reviews" value={stats.total} />
        <StatCard
          label="Avg Rating"
          value={stats.total ? `${stats.avgRating} / 5` : '—'}
          sub={stats.total ? <Stars rating={stats.avgRating} /> : null}
          color={stats.avgRating >= 4 ? '#039855' : stats.avgRating >= 3 ? '#f59e0b' : '#dc2626'}
        />
        <StatCard label="Total Complaints" value={complaintStats.total} color={complaintStats.total > 0 ? '#dc2626' : undefined} />
        <StatCard label="Pending Complaints" value={complaintStats.pendingComplaints} color={complaintStats.pendingComplaints > 0 ? '#f59e0b' : undefined} />
      </div>

      {/* Rating distribution */}
      {stats.total > 0 && (
        <div className="drp-distribution">
          {[5, 4, 3, 2, 1].map(star => {
            const count = stats.distribution[star] || 0;
            const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
            return (
              <div key={star} className="drp-dist-row">
                <span className="drp-dist-label">{star}★</span>
                <div className="drp-dist-bar-wrap">
                  <div className="drp-dist-bar" style={{ width: `${pct}%` }} />
                </div>
                <span className="drp-dist-count">{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="drp-controls">
        <div className="drp-tabs">
          {[['all', 'All'], ['ratings', 'Ratings Only'], ['complaints', 'Complaints']].map(([v, l]) => (
            <button key={v} className={`drp-tab ${tab === v ? 'drp-tab--active' : ''}`} onClick={() => setTab(v)}>{l}</button>
          ))}
        </div>
        <div className="drp-filters">
          {tab !== 'ratings' && (
            <select className="drp-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {COMPLAINT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          )}
          <input className="drp-search" placeholder="Search by driver, customer, comment…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="drp-content">
        {/* List */}
        <div className="drp-list">
          {loading && <div className="drp-list-loading">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="drp-list-empty">No feedback found.</div>}
          {filtered.map(r => (
            <div key={r.$id}
              className={`drp-card ${r.isComplaint ? 'drp-card--complaint' : ''} ${selectedRating?.$id === r.$id ? 'drp-card--selected' : ''}`}
              onClick={() => setSelectedRating(r)}
            >
              <div className="drp-card-top">
                <div className="drp-card-badges">
                  {r.isComplaint && <span className="drp-badge drp-badge--complaint">Complaint</span>}
                  {r.isComplaint && r.complaintStatus && (
                    <span className={`drp-badge drp-badge--status drp-badge--${r.complaintStatus}`}>
                      {r.complaintStatus}
                    </span>
                  )}
                  {!r.isComplaint && r.rating > 0 && <Stars rating={r.rating} />}
                </div>
                <span className="drp-card-date">{formatDate(r.ratedAt || r.$createdAt)}</span>
              </div>
              <p className="drp-card-comment">{r.comment || <em>No comment</em>}</p>
              <div className="drp-card-meta">
                <span>Driver: <strong>{r.driverId?.substring(0, 12) || '—'}</strong></span>
                {r.complaintType && <span>Type: <strong>{r.complaintType}</strong></span>}
              </div>
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {selectedRating && (
          <div className="drp-detail">
            <div className="drp-detail-header">
              <h3>{selectedRating.isComplaint ? 'Complaint Detail' : 'Rating Detail'}</h3>
              <button className="drp-detail-close" onClick={() => setSelectedRating(null)}>×</button>
            </div>

            <div className="drp-detail-body">
              {!selectedRating.isComplaint && selectedRating.rating > 0 && (
                <div className="drp-detail-row">
                  <span className="drp-detail-label">Rating</span>
                  <span><Stars rating={selectedRating.rating} size={20} /> {selectedRating.rating}/5</span>
                </div>
              )}
              {selectedRating.isComplaint && (
                <div className="drp-detail-row">
                  <span className="drp-detail-label">Complaint Type</span>
                  <span>{selectedRating.complaintType || '—'}</span>
                </div>
              )}
              <div className="drp-detail-row">
                <span className="drp-detail-label">Comment</span>
                <span>{selectedRating.comment || '—'}</span>
              </div>
              <div className="drp-detail-row">
                <span className="drp-detail-label">Driver ID</span>
                <span className="drp-mono">{selectedRating.driverId || '—'}</span>
              </div>
              <div className="drp-detail-row">
                <span className="drp-detail-label">Customer ID</span>
                <span className="drp-mono">{selectedRating.customerId || '—'}</span>
              </div>
              <div className="drp-detail-row">
                <span className="drp-detail-label">Request ID</span>
                <span className="drp-mono">{selectedRating.requestId || '—'}</span>
              </div>
              <div className="drp-detail-row">
                <span className="drp-detail-label">Submitted</span>
                <span>{formatDate(selectedRating.ratedAt || selectedRating.$createdAt)}</span>
              </div>

              {selectedRating.request && (
                <div className="drp-detail-request">
                  <p className="drp-detail-request-title">Linked Delivery Request</p>
                  <div className="drp-detail-row">
                    <span className="drp-detail-label">Store</span>
                    <span>{selectedRating.request.storeName || '—'}</span>
                  </div>
                  <div className="drp-detail-row">
                    <span className="drp-detail-label">Status</span>
                    <span>{selectedRating.request.status || '—'}</span>
                  </div>
                  <div className="drp-detail-row">
                    <span className="drp-detail-label">Package</span>
                    <span>{selectedRating.request.packageName || '—'}</span>
                  </div>
                </div>
              )}

              {selectedRating.isComplaint && (
                <div className="drp-detail-status-section">
                  <p className="drp-detail-label">Update Complaint Status</p>
                  <div className="drp-status-buttons">
                    {COMPLAINT_STATUSES.map(s => (
                      <button key={s}
                        className={`drp-status-btn drp-status-btn--${s} ${selectedRating.complaintStatus === s ? 'drp-status-btn--active' : ''}`}
                        disabled={updatingId === selectedRating.$id}
                        onClick={() => handleStatusChange(selectedRating.$id, s)}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="drp-detail-actions">
                <button className="drp-btn-danger" onClick={() => handleDelete(selectedRating.$id)}>
                  Delete this feedback
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryRatingsPage;
